import React, { useState, useEffect, useMemo } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  contactSubmissionsService,
  ContactSubmission,
  ContactSubmissionStatus,
} from '../../services/contactSubmissionsService';

const STATUS_LABELS: Record<ContactSubmissionStatus | 'all', string> = {
  all: 'All',
  new: 'New',
  read: 'Read',
  replied: 'Replied',
  archived: 'Archived',
};

const STATUS_COLORS: Record<ContactSubmissionStatus, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  read: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  replied: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

const AdminContactSubmissionsPage: React.FC = () => {
  const { isDark } = useAdminTheme();
  const { currentUser } = useAuth();

  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<ContactSubmission | null>(null);
  const [filterStatus, setFilterStatus] = useState<ContactSubmissionStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [note, setNote] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setIsLoading(true);
    try {
      const data = await contactSubmissionsService.getAll();
      setSubmissions(data);
    } catch (err) {
      console.error('Failed to load contact submissions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return submissions.filter(s => {
      const matchStatus = filterStatus === 'all' || s.status === filterStatus;
      const q = searchTerm.toLowerCase();
      const matchSearch = !q ||
        s.name.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.subject.toLowerCase().includes(q) ||
        s.message.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [submissions, filterStatus, searchTerm]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: submissions.length };
    submissions.forEach(s => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [submissions]);

  const handleSelect = async (s: ContactSubmission) => {
    setSelected(s);
    setNote(s.adminNote || '');
    // Auto-mark as read
    if (s.status === 'new') {
      await contactSubmissionsService.markAsRead(s.id);
      setSubmissions(prev => prev.map(x => x.id === s.id ? { ...x, status: 'read' } : x));
      setSelected(prev => prev?.id === s.id ? { ...prev, status: 'read' } : prev);
    }
  };

  const handleMarkReplied = async () => {
    if (!selected || !currentUser?.email) return;
    await contactSubmissionsService.markAsReplied(selected.id, currentUser.email);
    setSubmissions(prev => prev.map(x => x.id === selected.id ? { ...x, status: 'replied' } : x));
    setSelected(prev => prev ? { ...prev, status: 'replied' } : null);
  };

  const handleArchive = async () => {
    if (!selected) return;
    await contactSubmissionsService.archive(selected.id);
    setSubmissions(prev => prev.map(x => x.id === selected.id ? { ...x, status: 'archived' } : x));
    setSelected(prev => prev ? { ...prev, status: 'archived' } : null);
  };

  const handleSaveNote = async () => {
    if (!selected) return;
    setIsSavingNote(true);
    try {
      await contactSubmissionsService.saveNote(selected.id, note);
      setSubmissions(prev => prev.map(x => x.id === selected.id ? { ...x, adminNote: note } : x));
      setSelected(prev => prev ? { ...prev, adminNote: note } : null);
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Delete this submission? This cannot be undone.')) return;
    setIsDeleting(true);
    try {
      await contactSubmissionsService.delete(selected.id);
      setSubmissions(prev => prev.filter(x => x.id !== selected.id));
      setSelected(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const card = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const text = isDark ? 'text-white' : 'text-gray-900';
  const sub  = isDark ? 'text-gray-400' : 'text-gray-500';
  const inp  = isDark
    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className={`flex h-full min-h-0 overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>

      {/* ── Left: list panel ──────────────────────────────────────────────── */}
      <div className={`w-[380px] flex-shrink-0 flex flex-col border-r ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>

        {/* Header */}
        <div className={`px-4 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className={`text-sm font-semibold ${text}`}>
              Contact Submissions
              {counts.new > 0 && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-500 text-white">
                  {counts.new}
                </span>
              )}
            </h2>
            <button
              onClick={load}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search name, email, subject..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3 ${inp}`}
          />

          {/* Status filter tabs */}
          <div className="flex gap-1 flex-wrap">
            {(Object.keys(STATUS_LABELS) as (ContactSubmissionStatus | 'all')[]).map(s => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-2 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  filterStatus === s
                    ? 'bg-blue-600 text-white'
                    : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {STATUS_LABELS[s]} {counts[s] ? `(${counts[s]})` : ''}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className={`text-center py-12 text-xs ${sub}`}>No submissions found.</div>
          ) : (
            filtered.map(s => (
              <button
                key={s.id}
                onClick={() => handleSelect(s)}
                className={`w-full text-left px-4 py-3.5 border-b transition-colors ${
                  isDark ? 'border-gray-700/60' : 'border-gray-100'
                } ${
                  selected?.id === s.id
                    ? isDark ? 'bg-gray-700' : 'bg-blue-50'
                    : isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className={`text-[13px] font-medium truncate ${s.status === 'new' ? text : sub}`}>
                    {s.name}
                  </span>
                  <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[s.status]}`}>
                    {s.status}
                  </span>
                </div>
                <p className={`text-[12px] truncate mb-1 ${s.status === 'new' ? (isDark ? 'text-gray-300' : 'text-gray-700') : sub}`}>
                  {s.subject}
                </p>
                <p className={`text-[11px] ${sub}`}>{contactSubmissionsService.formatDate(s.createdAt)}</p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Right: detail panel ───────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {!selected ? (
          <div className={`flex flex-col items-center justify-center h-full ${sub}`}>
            <svg className="w-10 h-10 mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-sm">Select a submission to view</p>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto p-6 space-y-5">

            {/* Meta header */}
            <div className={`rounded-xl border p-5 ${card}`}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className={`text-base font-semibold ${text}`}>{selected.subject}</h3>
                  <p className={`text-xs mt-0.5 ${sub}`}>{contactSubmissionsService.formatDate(selected.createdAt)}</p>
                </div>
                <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selected.status]}`}>
                  {selected.status}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className={`text-[10px] uppercase tracking-wider ${sub} mb-0.5`}>From</p>
                  <p className={`text-sm font-medium ${text}`}>{selected.name}</p>
                </div>
                <div>
                  <p className={`text-[10px] uppercase tracking-wider ${sub} mb-0.5`}>Email</p>
                  <a href={`mailto:${selected.email}`} className="text-sm text-blue-500 hover:underline">{selected.email}</a>
                </div>
              </div>
            </div>

            {/* Message */}
            <div className={`rounded-xl border p-5 ${card}`}>
              <p className={`text-[10px] uppercase tracking-wider ${sub} mb-3`}>Message</p>
              <p className={`text-sm leading-relaxed whitespace-pre-wrap ${text}`}>{selected.message}</p>
            </div>

            {/* Actions */}
            <div className={`rounded-xl border p-5 ${card} flex flex-wrap gap-2`}>
              <a
                href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                onClick={handleMarkReplied}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Reply in email client
              </a>
              {selected.status !== 'replied' && (
                <button
                  onClick={handleMarkReplied}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                    isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Mark as replied
                </button>
              )}
              {selected.status !== 'archived' && (
                <button
                  onClick={handleArchive}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                    isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  Archive
                </button>
              )}
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>

            {/* Admin note */}
            <div className={`rounded-xl border p-5 ${card}`}>
              <p className={`text-[10px] uppercase tracking-wider ${sub} mb-2`}>Internal note</p>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={3}
                placeholder="Add a private note visible only to admins..."
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${inp}`}
              />
              <button
                onClick={handleSaveNote}
                disabled={isSavingNote}
                className="mt-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSavingNote ? 'Saving…' : 'Save note'}
              </button>
            </div>

            {/* Metadata */}
            {(selected.ipAddress || selected.userAgent) && (
              <div className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-[10px] uppercase tracking-wider ${sub} mb-2`}>Metadata</p>
                {selected.ipAddress && (
                  <p className={`text-xs ${sub}`}><span className="font-medium">IP:</span> {selected.ipAddress}</p>
                )}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContactSubmissionsPage;
